import { useCallback, useMemo, useState } from 'react';
import { GridCellProps } from 'react-virtualized';
import moment from 'moment';
import isNil from 'lodash/isNil';
import * as CohortSampleChartUI from '../CohortSampleChart/CohortSampleChartUI';
import * as CohortSampleTableUI from '../CohortSampleChart/CohortSampleTable/CohortSampleTableUI';
import CohortSampleTable from '../CohortSampleChart/CohortSampleTable';
import ThinDivider from '../../ThinDivider';
import { SecondaryButton } from '../../Button';
import {
  IInspectSubjectTableTableColumnFieldsFragment,
  ISubjectFieldsFragment,
  Order,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { exportTableToCsv } from '../../MultiGridTable';
import { displayTextWithEmptySymbol } from '../CohortSampleChart/utils';
import { globalFormat } from '../../../utils/formatting';
import KubitTooltip from '../../KubitTooltip';
import UserInspectViewFieldsFilter from './UserInspectViewFieldsFilter';
import { useDragResize } from '../../../utils/hooks/useDragResize';
import styled from 'styled-components';
import { TableSortIcon } from '../../Icon';
import { useChartContext } from '../ChartContext';
interface IProps {
  columns: IInspectSubjectTableTableColumnFieldsFragment[];
  subject: ISubjectFieldsFragment;
  exportUrl: string | null;
  schemaId: string;
  onSubmitFields: (fieldKeys: string[]) => void;
  presetTableHeight?: number;
}

const MIN_COLUMN_WIDTH = 120;
const DEFAULT_CELL_WIDTH = 160;
const COHORT_LAST_SEEN_DATE_FORMAT = 'M/D/YYYY HH:mm:ss:SSS';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  min-height: 0;
`;

const UserInspectViewTable = ({
  columns,
  subject,
  schemaId,
  exportUrl,
  onSubmitFields,
  presetTableHeight,
}: IProps) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<Order | null>(null);
  const [columnWidths, setColumnWidths] = useState(
    Array.from({ length: columns.length }).map(() => DEFAULT_CELL_WIDTH),
  );
  const [multiGridWidth, setMultiGridWidth] = useState<number>();

  const {
    settings: { inspectSubjectId },
  } = useChartContext();
  assertNotNil(inspectSubjectId);

  const { headers, rows } = useMemo(() => {
    const headers = columns.map((column) => column.header);
    const numRows = columns[0].cells.length;
    const rows = Array.from({ length: numRows }, (_, rowIndex) =>
      columns.map((column) => column.cells[rowIndex]),
    );

    return {
      headers,
      rows,
    };
  }, [columns]);
  const sortedRows = useMemo(() => {
    let result = [...rows];

    if (searchTerm?.length) {
      result = result.filter((row) =>
        // Check if any cell in the row includes the searchTerm
        row.some((cell) => {
          const stringValue = cell.value.toString();
          return (
            stringValue.length &&
            stringValue.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }),
      );
    }

    // Sort the filtered data based on sortColumnIndex and sortDirection
    if (!isNil(sortColumnIndex)) {
      result.sort((a, b) => {
        const aValue = a[sortColumnIndex].value;
        const bValue = b[sortColumnIndex].value;

        return sortDirection === Order.Asc
          ? aValue.localeCompare(bValue, 'en', { numeric: true })
          : bValue.localeCompare(aValue, 'en', { numeric: true });
      });
    }

    // Return the filtered and sorted data for rendering
    return result;
  }, [sortColumnIndex, sortDirection, rows, searchTerm]);
  const selectedFieldKeys = useMemo(
    () => headers.map((header) => header.field.key),
    [headers],
  );

  const headersCount = headers.length;
  const rowCount = sortedRows.length;

  const getColumnWidth = useCallback(
    ({ index }: { index: number }) => {
      return columnWidths[index];
    },
    [columnWidths],
  );
  const handleResizeProgress = useCallback(
    (e: MouseEvent, refIndex: number) => {
      const deltaX = e.movementX;
      setColumnWidths((prevWidths: number[]) => {
        const newWidths = [...prevWidths];

        const currentWidth = newWidths[refIndex];
        const newWidth = newWidths[refIndex] + deltaX;

        const draftWidths = [...newWidths];
        draftWidths[refIndex] = newWidth;

        const totalNewWidths = draftWidths.reduce((a, b) => a + b, 0);

        if (multiGridWidth && totalNewWidths + 8 < multiGridWidth) {
          newWidths[refIndex] = currentWidth;
        } else {
          newWidths[refIndex] =
            newWidth >= MIN_COLUMN_WIDTH ? newWidth : MIN_COLUMN_WIDTH;
        }
        return newWidths;
      });
    },
    [multiGridWidth],
  );
  const { handleResizeStart } = useDragResize({
    onResizeProgress: handleResizeProgress,
  });

  const handleSort = useCallback((columnIndex: number | null) => {
    setSortColumnIndex(columnIndex);

    if (columnIndex === null) {
      setSortDirection(null);
    } else {
      setSortDirection((prevState) =>
        prevState === Order.Asc ? Order.Desc : Order.Asc,
      );
    }
  }, []);

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: GridCellProps) => {
      if (rowIndex === 0) {
        const { field } = headers[columnIndex];
        return (
          <CohortSampleTableUI.HeaderCell
            key={key}
            style={style}
            $columnIndex={columnIndex}
          >
            <CohortSampleTableUI.TableHeaderRow
              onClick={() => handleSort(columnIndex)}
            >
              <CohortSampleChartUI.FlexColumn>
                <CohortSampleChartUI.HeaderTextWrapper>
                  {field.name}
                </CohortSampleChartUI.HeaderTextWrapper>
                {sortColumnIndex === columnIndex && !isNil(sortDirection) && (
                  <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
                )}
              </CohortSampleChartUI.FlexColumn>
            </CohortSampleTableUI.TableHeaderRow>
            {rowIndex === 0 && (
              <CohortSampleTableUI.TableColumnResizeHandle
                onMouseDown={(e) => handleResizeStart(e, columnIndex)}
              />
            )}
          </CohortSampleTableUI.HeaderCell>
        );
      }

      const cellData = sortedRows[rowIndex - 1][columnIndex];

      if (cellData) {
        if (columnIndex === 0) {
          return (
            <CohortSampleTableUI.Cell
              key={key}
              style={style}
              title={cellData.value}
              $rowIndex={rowIndex}
              $columnIndex={columnIndex}
            >
              {displayTextWithEmptySymbol(cellData.value)}
            </CohortSampleTableUI.Cell>
          );
        }

        const isCellValueDate = moment(
          cellData.value,
          // special case, will change after kobol update schema for table to indicate which cell is date, and which
          // is number or string
          COHORT_LAST_SEEN_DATE_FORMAT,
          true,
        ).isValid();

        const getParsedDisplayValue = () => {
          if (isCellValueDate) {
            return globalFormat.formatDate(
              cellData.value,
              {
                dateFormat,
                timeFormat,
              },
              COHORT_LAST_SEEN_DATE_FORMAT,
            );
          } else {
            return cellData.value;
          }
        };
        const parsedValue = getParsedDisplayValue();
        return (
          <KubitTooltip
            placement="top-start"
            key={key}
            noOpenDelay={true}
            content={cellData.value === 'inf' ? 'Infinite' : parsedValue}
          >
            <CohortSampleTableUI.Cell
              style={style}
              $rowIndex={rowIndex}
              $columnIndex={columnIndex}
            >
              {cellData.value === 'inf' ? (
                <CohortSampleTableUI.InfinityContainer>
                  &infin;
                </CohortSampleTableUI.InfinityContainer>
              ) : (
                parsedValue
              )}
            </CohortSampleTableUI.Cell>
          </KubitTooltip>
        );
      }
    },
    [
      dateFormat,
      handleResizeStart,
      headers,
      handleSort,
      sortColumnIndex,
      sortDirection,
      sortedRows,
      timeFormat,
    ],
  );

  const showReset = searchTerm.length > 0 || !isNil(sortColumnIndex);
  const handleReset = () => {
    setSearchTerm('');
    handleSort(null);
  };

  const onExportToCsv = () => {
    const data = sortedRows.map((row) => row.map((cell) => cell.value));
    exportTableToCsv({
      display: `${subject.name}-${inspectSubjectId}`,
      headers: headers.map((h) => h.field.name),
      data,
    });
  };

  return (
    <Container>
      <CohortSampleChartUI.FlexRow>
        <CohortSampleTableUI.SearchInputContainer>
          <CohortSampleTableUI.StyledSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <ThinDivider layout="vertical" size="short" />
          <UserInspectViewFieldsFilter
            schemaId={schemaId}
            selectedFieldKeys={selectedFieldKeys}
            onSubmit={onSubmitFields}
          />
          {searchTerm.length > 0 && (
            <CohortSampleTableUI.SearchResultText>
              Found <strong>{rowCount}</strong> results.
            </CohortSampleTableUI.SearchResultText>
          )}
        </CohortSampleTableUI.SearchInputContainer>
        <CohortSampleTableUI.FlexRow>
          {showReset && (
            <KubitTooltip content="Reset to default view" noOpenDelay={true}>
              <div>
                <SecondaryButton
                  type="button"
                  onClick={handleReset}
                  isOutlined
                  trackContext="cohorts"
                  trackAction="inspect table - reset"
                >
                  Reset
                </SecondaryButton>
              </div>
            </KubitTooltip>
          )}
          {rowCount > 0 && (
            <KubitTooltip
              content="Export current table to CSV"
              noOpenDelay={true}
            >
              <div>
                <SecondaryButton
                  type="button"
                  onClick={onExportToCsv}
                  isOutlined
                  trackContext="cohorts"
                  trackAction="inspect table - export table csv"
                >
                  Export Table to CSV
                </SecondaryButton>
              </div>
            </KubitTooltip>
          )}
          {!isNil(exportUrl) && (
            <KubitTooltip content="Export all data to CSV" noOpenDelay={true}>
              <div>
                <SecondaryButton
                  type="button"
                  onClick={() => window.location.assign(exportUrl)}
                  isOutlined
                  trackContext="cohorts"
                  trackAction="inspect table - export data csv"
                >
                  Export Data to CSV
                </SecondaryButton>
              </div>
            </KubitTooltip>
          )}
        </CohortSampleTableUI.FlexRow>
      </CohortSampleChartUI.FlexRow>
      <CohortSampleTable
        cellRenderer={cellRenderer}
        headersCount={headersCount}
        columnCount={columns.length}
        rowCount={rowCount}
        columnWidths={columnWidths}
        getColumnWidth={getColumnWidth}
        multiGridWidth={multiGridWidth}
        onChangeColumnWidths={setColumnWidths}
        setMultiGridWidth={setMultiGridWidth}
        presetTableHeight={presetTableHeight}
      />
    </Container>
  );
};

export default UserInspectViewTable;
